import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  input,
  output,
  signal,
  effect,
  inject,
  model,
  computed,
  Signal,
  TemplateRef,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  startWith,
} from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";

type Directions = "bottom" | "top" | "left" | "right";

@Component({
  selector: "app-dropdown",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./dropdown.component.html",
  styleUrls: ["./dropdown.component.scss"],
  host: {
    class: "min-w-0",
  },
})
export class DropdownComponent<T, K extends keyof T> {
  private readonly host = inject(ElementRef);

  constructor() {
    // Close dropdown if click outside
    effect(() => {
      const sub = fromEvent(document, "click").subscribe((event) => {
        const clickedInside = this.host.nativeElement.contains(
          event.target as Node,
        );
        if (!clickedInside) this.dropdownHidden.set(true);
      });

      return () => sub.unsubscribe();
    });
  }
  readonly customButtonTemplate = input<TemplateRef<HTMLDivElement> | null>(
    null,
  );
  readonly overwriteStyle = input<string>("");
  readonly overwriteButtonStyle = input<string>("");
  readonly borderless = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly dropdownHidden = signal<boolean>(true);
  readonly dropdownPlacement = input<Directions>("top");
  readonly dropdownXPlacement = input<Directions>("right");
  readonly hasNoneOption = input<boolean>(false); // if [None] should be an aviable option in the dropdown - Will be filled in
  readonly noneOutputValue = input<any>(null); // What should we use to output when user select [None]
  readonly noneLabel = input<string>("None"); // Label to show for the [None] option
  readonly useSearch = input(false); // Enable searchbar
  readonly searchControl = new FormControl("");
  readonly searchPlaceholder = input("Search");
  readonly options = input.required<T[]>(); // List of all dropdown options user can choose
  readonly keyField = input.required<K>(); // Field of an option to be use as the key
  readonly labelField = input.required<K>(); // Field to be use as the displaying label
  readonly outputField = input.required<K>(); // Field to be use as the output value emitting back from user picking an option
  readonly selectedKey = model<T[K] | null>(null); // The current selected option's key
  readonly outputPseudoEvent = output<{ target: { value: T[K] | null } }>({
    alias: "onClickOption",
  });

  /**
   * Create a mapping of [key] = [option object] - Use for direct look up when user select the option
   */
  private readonly optionsMap = computed(() =>
    this.options().reduce(
      (acc, item) => {
        const key = item[this.keyField()];
        acc[key as string | number] = item;
        return acc;
      },
      {} as Record<string | number, T>,
    ),
  );

  /**
   * Get the option object using selected key
   */
  readonly selectedOption = computed(() => {
    const key = this.selectedKey();
    return key !== null && key !== undefined ?
        this.optionsMap()[String(key) as string | number]
      : null;
  });

  /**
   * Get the label of the selected option to display on the frontend
   */
  readonly selectedLabel = computed(() => {
    const opt = this.selectedOption();
    return opt ? String(opt[this.labelField()]) : this.noneLabel();
  });

  /**
   * Create [None] as an option for the dropdown. The key for that option will be null
   */
  private readonly noneOptions: Signal<T> = computed(
    () =>
      ({
        ...Object.fromEntries(
          Object.keys(this.options()[0] || {}).map((k) => [k, null]),
        ),
        [this.labelField()]: this.noneLabel(),
        [this.keyField()]: null,
      }) as T,
  );

  /**
   * The options to display back to the frontend, with search applied
   */

  readonly filteredOptions$ = combineLatest([
    toObservable(this.options),
    this.searchControl.valueChanges.pipe(
      debounceTime(200), // only debounce keystroke updates
      startWith(""), // emit immediately on subscription so the list shows up
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([allOptions, userInput]) => {
      const searchTerm = (userInput || "").toLowerCase().trim();
      // If not using search, return all options
      const filtered =
        this.useSearch() ?
          allOptions.filter((opt) =>
            String(opt[this.labelField()]).toLowerCase().includes(searchTerm),
          )
        : allOptions;
      // Add the (None) option at the top
      if (this.hasNoneOption()) return [this.noneOptions(), ...filtered];
      else return filtered;
    }),
  );

  /**
   * Emit a fake 'Event' to the parent - temporary way to make it work with the updateForm function in the parent implementation
   */
  readonly onClickOption = (option: T) => {
    const valueSelected = option[this.keyField()];
    this.selectedKey.set(option[this.keyField()]);
    const selectNewOptEvent = {
      target: {
        value:
          valueSelected !== null ? option[this.outputField()]
          : (
            this.hasNoneOption() // If we have [None] option, we will output the value from noneOutputValue input else null
          ) ?
            this.noneOutputValue()
          : null,
      },
    };
    this.outputPseudoEvent.emit(selectNewOptEvent);
    this.dropdownHidden.set(true); // also close the dropdown after selecting an option
  };

  /**
   * Get the label from the option object
   */
  getLabel = (option: T) => {
    return String(option[this.labelField()]);
  };

  trackByKey = (option: T) => {
    return option[this.keyField()];
  };
}
